import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Building2, ChevronRight, Trees, Image as ImageIcon, Pencil, Star, StarOff, Upload, EyeOff, Eye, MoveUp, MoveDown } from "lucide-react";
import { toast } from "sonner";

// F-MOTOR-RESERVAS.2.A — catálogo de comodidades (slugs + labels PT-BR).
// Deve espelhar `COMODIDADES_POUSADA` em backend/server.py.
const COMODIDADES_POUSADA = [
  { slug: "piscina", label: "Piscina" },
  { slug: "jacuzzi", label: "Jacuzzi" },
  { slug: "sauna", label: "Sauna" },
  { slug: "churrasqueira", label: "Churrasqueira" },
  { slug: "salao_jogos", label: "Salão de Jogos" },
  { slug: "playground", label: "Playground" },
  { slug: "horta", label: "Horta" },
  { slug: "pet_friendly", label: "Pet Friendly" },
  { slug: "estacionamento_coberto", label: "Estacionamento Coberto" },
  { slug: "wifi_areas_comuns", label: "Wi-Fi em Áreas Comuns" },
  { slug: "cafe_manha_incluso", label: "Café da Manhã Incluso" },
  { slug: "restaurante", label: "Restaurante" },
  { slug: "bar", label: "Bar" },
  { slug: "trilhas", label: "Trilhas" },
  { slug: "lareira", label: "Lareira" },
  { slug: "espaco_eventos", label: "Espaço para Eventos" },
  { slug: "acessibilidade", label: "Acessibilidade" },
  { slug: "familia", label: "Família" },
  { slug: "romantico", label: "Romântico" },
];

const DESCRICAO_CURTA_MAX = 160;
const DESCRICAO_MAX = 2000;

const EMPTY_FORM = {
  nome: "",
  endereco: "",
  telefone: "",
  email: "",
  is_chacara: false,
  bloqueia_suites_dia_unico_padrao: true,
  permite_reserva_inteira: false,
  descricao: "",
  descricao_curta: "",
  comodidades: [],
  slug: "",
};

// F-MOTOR-RESERVAS.2.C — Galeria de fotos no admin.
// Componente simples, mantido no mesmo arquivo (padrão do projeto, sem over-engineering).
const FOTO_MIME_ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const FOTO_MAX_BYTES = 5 * 1024 * 1024;

function PousadaFotosGallery({ pousadaId }) {
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pousadas/${pousadaId}/fotos`, {
        params: { incluir_inativas: true },
      });
      setFotos(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar fotos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (pousadaId) load(); /* eslint-disable-next-line */ }, [pousadaId]);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    let okCount = 0;
    for (const file of files) {
      if (!FOTO_MIME_ALLOWED.includes(file.type)) {
        toast.error(`${file.name}: formato não permitido (JPG/PNG/WebP)`);
        continue;
      }
      if (file.size > FOTO_MAX_BYTES) {
        toast.error(`${file.name}: arquivo maior que 5 MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/pousadas/${pousadaId}/fotos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        okCount += 1;
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Erro ao enviar ${file.name}`);
      }
    }
    if (okCount > 0) toast.success(`${okCount} foto(s) enviada(s)`);
    setUploading(false);
    load();
  };

  const definirPrincipal = async (foto) => {
    try {
      await api.patch(`/pousadas/${pousadaId}/fotos/${foto.id}`, { principal: true });
      toast.success("Foto principal atualizada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao definir principal");
    }
  };

  const toggleAtivo = async (foto) => {
    try {
      await api.patch(`/pousadas/${pousadaId}/fotos/${foto.id}`, { ativo: !foto.ativo });
      toast.success(foto.ativo ? "Foto desativada" : "Foto ativada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar status");
    }
  };

  const salvarLegenda = async (foto, legenda) => {
    if ((legenda || "") === (foto.legenda || "")) return;
    try {
      await api.patch(`/pousadas/${pousadaId}/fotos/${foto.id}`, { legenda });
      toast.success("Legenda salva");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar legenda");
    }
  };

  const excluir = async (foto) => {
    if (!window.confirm("Excluir esta foto? Esta ação é permanente.")) return;
    try {
      await api.delete(`/pousadas/${pousadaId}/fotos/${foto.id}`);
      toast.success("Foto removida");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao remover foto");
    }
  };

  const mover = async (foto, dir) => {
    const ativasOrdenadas = [...fotos].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
    const idx = ativasOrdenadas.findIndex((f) => f.id === foto.id);
    const alvo = idx + dir;
    if (idx < 0 || alvo < 0 || alvo >= ativasOrdenadas.length) return;
    const nova = ativasOrdenadas.slice();
    [nova[idx], nova[alvo]] = [nova[alvo], nova[idx]];
    try {
      await api.patch(`/pousadas/${pousadaId}/fotos/reordenar`, {
        ordem: nova.map((f) => f.id),
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao reordenar");
    }
  };

  const fotosOrdenadas = [...fotos].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const fotoUrl = (f) => `${API_BASE}/api/public/fotos/${f.id}`;

  return (
    <div data-testid="pousada-fotos-gallery" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[12px] text-stone-500">
          JPG, PNG ou WebP · até 5 MB por arquivo · a primeira foto vira principal automaticamente.
        </div>
        <label
          data-testid="btn-upload-foto"
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm cursor-pointer border transition-colors ${
            uploading
              ? "bg-stone-100 border-stone-200 text-stone-400 cursor-not-allowed"
              : "bg-[#083A6B] hover:bg-[#052B50] text-white border-[#083A6B]"
          }`}
        >
          <Upload size={14} />
          {uploading ? "Enviando..." : "Adicionar fotos"}
          <input
            type="file"
            data-testid="input-upload-foto"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            disabled={uploading}
            onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
          />
        </label>
      </div>

      {loading ? (
        <div className="text-sm text-stone-500 py-8 text-center">Carregando...</div>
      ) : fotosOrdenadas.length === 0 ? (
        <div
          data-testid="pousada-fotos-empty"
          className="border-2 border-dashed border-stone-300 rounded-md p-8 text-center bg-stone-50"
        >
          <ImageIcon size={28} className="mx-auto text-stone-400 mb-3" />
          <div className="font-serif text-lg text-stone-700">Nenhuma foto ainda</div>
          <div className="text-sm text-stone-500 mt-1">Clique em "Adicionar fotos" para começar.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {fotosOrdenadas.map((f, idx) => (
            <div
              key={f.id}
              data-testid={`foto-card-${f.id}`}
              data-principal={f.principal ? "true" : "false"}
              data-ativo={f.ativo ? "true" : "false"}
              className={`relative border rounded-md overflow-hidden bg-white group ${
                f.principal ? "border-[#083A6B] ring-2 ring-[#083A6B]/30" : "border-stone-200"
              } ${!f.ativo ? "opacity-60" : ""}`}
            >
              <div className="aspect-video bg-stone-100">
                <img
                  src={fotoUrl(f)}
                  alt={f.legenda || f.nome_original || "Foto"}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              {f.principal && (
                <span
                  data-testid={`foto-badge-principal-${f.id}`}
                  className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#083A6B] text-white px-2 py-0.5 rounded-full"
                >
                  <Star size={10} /> Principal
                </span>
              )}
              {!f.ativo && (
                <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest bg-stone-700 text-white px-2 py-0.5 rounded-full">
                  Inativa
                </span>
              )}
              <div className="p-2 space-y-2">
                <Input
                  data-testid={`input-foto-legenda-${f.id}`}
                  defaultValue={f.legenda || ""}
                  placeholder="Legenda (opcional)"
                  className="h-8 text-xs"
                  onBlur={(e) => salvarLegenda(f, e.target.value)}
                />
                <div className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      data-testid={`btn-mover-up-${f.id}`}
                      disabled={idx === 0}
                      onClick={() => mover(f, -1)}
                      className="text-stone-500 hover:text-[#083A6B] p-1 disabled:opacity-30"
                      aria-label="Mover para cima"
                    >
                      <MoveUp size={14} />
                    </button>
                    <button
                      type="button"
                      data-testid={`btn-mover-down-${f.id}`}
                      disabled={idx === fotosOrdenadas.length - 1}
                      onClick={() => mover(f, 1)}
                      className="text-stone-500 hover:text-[#083A6B] p-1 disabled:opacity-30"
                      aria-label="Mover para baixo"
                    >
                      <MoveDown size={14} />
                    </button>
                  </div>
                  <div className="flex items-center gap-1">
                    {!f.principal && f.ativo && (
                      <button
                        type="button"
                        data-testid={`btn-set-principal-${f.id}`}
                        onClick={() => definirPrincipal(f)}
                        className="text-stone-500 hover:text-[#083A6B] p-1"
                        aria-label="Definir como principal"
                        title="Definir como principal"
                      >
                        <StarOff size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`btn-toggle-ativo-${f.id}`}
                      onClick={() => toggleAtivo(f)}
                      className="text-stone-500 hover:text-[#083A6B] p-1"
                      aria-label={f.ativo ? "Desativar" : "Ativar"}
                      title={f.ativo ? "Desativar" : "Ativar"}
                    >
                      {f.ativo ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button
                      type="button"
                      data-testid={`btn-delete-foto-${f.id}`}
                      onClick={() => excluir(f)}
                      className="text-stone-500 hover:text-[#D4A652] p-1"
                      aria-label="Excluir"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function Pousadas() {
  const [items, setItems] = useState([]);
  const [quartosMap, setQuartosMap] = useState({});
  const [pousadaStats, setPousadaStats] = useState({});
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const comodidadesSet = useMemo(() => new Set(form.comodidades || []), [form.comodidades]);

  // F-FIX.PACOTE.4 · Correção 1 — ordenação APENAS visual (não altera registros/IDs).
  // Prioridade: "Pousada Cabreúva" → "Pousada Cabreúva 2" → "Cabreúva 3" → "Cabreúva 4 (Cida)" →
  // demais em ordem alfabética. Não casa com "Pousada Central de Cabreúva".
  const rankPousada = (nome) => {
    const n = String(nome || "").trim();
    const m = n.match(/^Pousada\s+Cabreúva(?:\s+(\d+))?(?:\s|\(|$)/i);
    if (!m) return [1000, n.toLowerCase()];
    const num = m[1] ? parseInt(m[1], 10) : 1; // "Cabreúva" sem número vira 1
    return [num, n.toLowerCase()];
  };
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const [ra, na] = rankPousada(a?.nome);
      const [rb, nb] = rankPousada(b?.nome);
      if (ra !== rb) return ra - rb;
      return na < nb ? -1 : na > nb ? 1 : 0;
    });
  }, [items]);

  const load = async () => {
    const [{ data: p }, { data: q }, dash] = await Promise.all([
      api.get("/pousadas"),
      api.get("/quartos"),
      api.get("/dashboard").catch(() => ({ data: null })),
    ]);
    setItems(p);
    const m = {};
    q.forEach((x) => { m[x.pousada_id] = (m[x.pousada_id] || 0) + 1; });
    setQuartosMap(m);
    const stats = {};
    (dash?.data?.ocupacao_pousadas || []).forEach((op) => { stats[op.pousada_id] = op; });
    setPousadaStats(stats);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      nome: p.nome || "",
      endereco: p.endereco || "",
      telefone: p.telefone || "",
      email: p.email || "",
      is_chacara: !!p.is_chacara,
      bloqueia_suites_dia_unico_padrao: p.bloqueia_suites_dia_unico_padrao !== false,
      permite_reserva_inteira: !!p.permite_reserva_inteira,
      descricao: p.descricao || "",
      descricao_curta: p.descricao_curta || "",
      comodidades: Array.isArray(p.comodidades) ? [...p.comodidades] : [],
      slug: p.slug || "",
    });
    setOpen(true);
  };

  const toggleComodidade = (slug) => {
    setForm((f) => {
      const has = f.comodidades.includes(slug);
      return {
        ...f,
        comodidades: has ? f.comodidades.filter((s) => s !== slug) : [...f.comodidades, slug],
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.error("Informe o nome da pousada");
    if ((form.descricao_curta || "").length > DESCRICAO_CURTA_MAX)
      return toast.error(`Descrição curta excede ${DESCRICAO_CURTA_MAX} caracteres`);
    if ((form.descricao || "").length > DESCRICAO_MAX)
      return toast.error(`Descrição excede ${DESCRICAO_MAX} caracteres`);
    try {
      if (editingId) {
        await api.patch(`/pousadas/${editingId}`, form);
        toast.success("Pousada atualizada");
      } else {
        await api.post("/pousadas", form);
        toast.success("Pousada cadastrada");
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      setOpen(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? String(detail) : "Erro ao salvar pousada");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta pousada? Todos os quartos e reservas relacionados serão removidos.")) return;
    try {
      await api.delete(`/pousadas/${id}`);
      toast.success("Pousada removida");
      load();
    } catch {
      toast.error("Erro ao remover");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Rede de acomodações"
        title="Pousadas"
        description={`Gerencie as ${items.length || ""} pousadas da rede. Clique em um cartão para ver a ocupação em tempo real.`.replace("as  pousadas", "as pousadas")}
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY_FORM); } }}>
            <DialogTrigger asChild>
              <Button data-testid="btn-nova-pousada" onClick={openCreate} className="bg-[#083A6B] hover:bg-[#052B50] text-white rounded-md">
                <Plus size={16} className="mr-2" /> Nova pousada
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">
                  {editingId ? "Editar pousada" : "Nova pousada"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="form-pousada">
                <Tabs defaultValue="dados" className="w-full">
                  <TabsList className="grid grid-cols-4 w-full">
                    <TabsTrigger value="dados" data-testid="tab-pousada-dados">Dados</TabsTrigger>
                    <TabsTrigger value="descricao" data-testid="tab-pousada-descricao">Descrição</TabsTrigger>
                    <TabsTrigger value="comodidades" data-testid="tab-pousada-comodidades">Comodidades</TabsTrigger>
                    <TabsTrigger value="fotos" data-testid="tab-pousada-fotos">Fotos</TabsTrigger>
                  </TabsList>

                  {/* -------- Dados -------- */}
                  <TabsContent value="dados" className="space-y-4 pt-4">
                    <div>
                      <Label htmlFor="nome">Nome</Label>
                      <Input id="nome" data-testid="input-pousada-nome" value={form.nome}
                        onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                    </div>
                    <div>
                      <Label htmlFor="endereco">Endereço</Label>
                      <Input id="endereco" data-testid="input-pousada-endereco" value={form.endereco}
                        onChange={(e) => setForm({ ...form, endereco: e.target.value })} />
                    </div>
                    <div>
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input id="telefone" data-testid="input-pousada-telefone" value={form.telefone}
                        onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
                    </div>
                    <div>
                      <Label htmlFor="email">E-mail</Label>
                      <Input id="email" type="email" data-testid="input-pousada-email" value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                    {editingId && (
                      <div>
                        <Label htmlFor="slug">Slug (URL amigável)</Label>
                        <Input id="slug" data-testid="input-pousada-slug" value={form.slug}
                          onChange={(e) => setForm({ ...form, slug: e.target.value })}
                          placeholder="pousada-exemplo (deixe vazio para regenerar)" />
                        <div className="text-[11px] text-stone-500 mt-1">
                          Minúsculas, sem acento e com hífen. Será validado contra duplicados.
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between border border-stone-200 rounded-md p-3">
                      <div>
                        <div className="text-sm font-medium text-stone-800 flex items-center gap-2">
                          <Trees size={14} className="text-[#083A6B]" /> É chácara?
                        </div>
                        <div className="text-[11px] text-stone-500">
                          Ativa o modo "Chácara Inteira × Quartos individuais". As duas modalidades bloqueiam uma à outra em períodos sobrepostos.
                        </div>
                      </div>
                      <Switch
                        data-testid="switch-pousada-chacara"
                        checked={form.is_chacara}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, is_chacara: v }))}
                      />
                    </div>
                    {form.is_chacara && (
                      <div className="flex items-center justify-between border border-stone-200 rounded-md p-3 bg-[#EAF0EA]/40">
                        <div>
                          <div className="text-sm font-medium text-stone-800">
                            Bloquear suítes por padrão em diárias avulsas (1 dia)
                          </div>
                          <div className="text-[11px] text-stone-500">
                            Chácara Inteira em <strong>1 diária (09:00–19:00)</strong> pode ou não bloquear as suítes. Este é o padrão sugerido — o admin ainda pode alternar na reserva.
                          </div>
                        </div>
                        <Switch
                          data-testid="switch-pousada-padrao-bloqueio-1dia"
                          checked={form.bloqueia_suites_dia_unico_padrao}
                          onCheckedChange={(v) => setForm((f) => ({ ...f, bloqueia_suites_dia_unico_padrao: v }))}
                        />
                      </div>
                    )}
                    <div className={`flex items-center justify-between border rounded-md p-3 ${form.is_chacara ? "border-stone-200 bg-stone-100 opacity-60" : "border-stone-200"}`}>
                      <div>
                        <div className="text-sm font-medium text-stone-800 flex items-center gap-2">
                          <Building2 size={14} className="text-[#083A6B]" /> Permitir reserva da pousada inteira
                        </div>
                        <div className="text-[11px] text-stone-500">
                          {form.is_chacara
                            ? "Indisponível para chácaras — elas já usam o modo Chácara Inteira × Quartos."
                            : "Habilita a criação de uma reserva que ocupa todos os quartos da pousada simultaneamente. Container deve ser cadastrado depois em Quartos."}
                        </div>
                      </div>
                      <Switch
                        data-testid="switch-pousada-permite-reserva-inteira"
                        checked={!!form.permite_reserva_inteira && !form.is_chacara}
                        disabled={form.is_chacara}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, permite_reserva_inteira: v }))}
                      />
                    </div>
                  </TabsContent>

                  {/* -------- Descrição -------- */}
                  <TabsContent value="descricao" className="space-y-4 pt-4">
                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="descricao_curta">Descrição curta</Label>
                        <span data-testid="count-descricao-curta" className="text-[11px] text-stone-500">
                          {(form.descricao_curta || "").length}/{DESCRICAO_CURTA_MAX}
                        </span>
                      </div>
                      <Input
                        id="descricao_curta"
                        data-testid="input-pousada-descricao-curta"
                        value={form.descricao_curta}
                        maxLength={DESCRICAO_CURTA_MAX}
                        onChange={(e) => setForm({ ...form, descricao_curta: e.target.value })}
                        placeholder="Resumo exibido nos cards do site (máx. 160)"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="descricao">Descrição</Label>
                        <span data-testid="count-descricao" className="text-[11px] text-stone-500">
                          {(form.descricao || "").length}/{DESCRICAO_MAX}
                        </span>
                      </div>
                      <Textarea
                        id="descricao"
                        data-testid="input-pousada-descricao"
                        value={form.descricao}
                        maxLength={DESCRICAO_MAX}
                        onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                        placeholder="Descrição completa (texto simples)."
                        rows={10}
                      />
                    </div>
                  </TabsContent>

                  {/* -------- Comodidades -------- */}
                  <TabsContent value="comodidades" className="space-y-3 pt-4">
                    <div className="text-[12px] text-stone-500">
                      Selecione as comodidades disponíveis. Elas aparecerão nas páginas do site.
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="list-pousada-comodidades">
                      {COMODIDADES_POUSADA.map((c) => {
                        const on = comodidadesSet.has(c.slug);
                        return (
                          <button
                            key={c.slug}
                            type="button"
                            data-testid={`chip-comodidade-${c.slug}`}
                            data-selected={on ? "true" : "false"}
                            onClick={() => toggleComodidade(c.slug)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                              on
                                ? "bg-[#083A6B] text-white border-[#083A6B]"
                                : "bg-white text-stone-700 border-stone-300 hover:border-[#083A6B]"
                            }`}
                          >
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                  </TabsContent>

                  {/* -------- Fotos (F-MOTOR-RESERVAS.2.C) -------- */}
                  <TabsContent value="fotos" className="pt-4">
                    {editingId ? (
                      <PousadaFotosGallery pousadaId={editingId} />
                    ) : (
                      <div
                        data-testid="placeholder-pousada-fotos"
                        className="border-2 border-dashed border-stone-300 rounded-md p-8 text-center bg-stone-50"
                      >
                        <ImageIcon size={28} className="mx-auto text-stone-400 mb-3" />
                        <div className="font-serif text-lg text-stone-700">Salve a pousada primeiro</div>
                        <div className="text-sm text-stone-500 mt-1">
                          O gerenciamento de fotos é habilitado após o cadastro inicial.
                        </div>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                <DialogFooter>
                  <Button type="submit" data-testid="btn-submit-pousada" className="bg-[#083A6B] hover:bg-[#052B50] text-white">
                    {editingId ? "Salvar alterações" : "Cadastrar"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedItems.map((p) => (
          <div
            key={p.id}
            data-testid={`pousada-card-${p.id}`}
            className="block bg-[#FFFFFF] border border-stone-200 rounded-lg p-6 hover:border-[#083A6B] hover:shadow-sm transition-all group relative"
          >
            <Link to={`/admin/pousadas/${p.id}/ocupacao`} className="block">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-md flex items-center justify-center ${p.is_chacara ? "bg-[#EAF0EA] text-[#083A6B]" : "bg-[#EAF0EA] text-[#083A6B]"}`}>
                  {p.is_chacara ? <Trees size={20} strokeWidth={1.6} /> : <Building2 size={20} strokeWidth={1.6} />}
                </div>
                <div className="flex items-center gap-2">
                  {p.is_chacara && (
                    <span
                      data-testid={`badge-chacara-${p.id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20 px-2 py-1 rounded-full"
                    >
                      <Trees size={10} /> Chácara
                    </span>
                  )}
                  {p.permite_reserva_inteira && !p.is_chacara && (
                    <span
                      data-testid={`badge-pousada-inteira-${p.id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#FDF3E3] text-[#8B6A1F] border border-[#8B6A1F]/20 px-2 py-1 rounded-full"
                    >
                      <Building2 size={10} /> Pousada inteira
                    </span>
                  )}
                  <button
                    type="button"
                    data-testid={`btn-edit-pousada-${p.id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(p); }}
                    className="text-stone-400 hover:text-[#083A6B] p-1 rounded transition-colors"
                    aria-label="Editar"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    type="button"
                    data-testid={`btn-delete-pousada-${p.id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(p.id); }}
                    className="text-stone-400 hover:text-[#D4A652] p-1 rounded transition-colors"
                    aria-label="Excluir"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="font-serif text-2xl text-stone-900 leading-tight mb-2 group-hover:text-[#083A6B]">{p.nome}</div>
              {p.descricao_curta && (
                <div data-testid={`pousada-descricao-curta-${p.id}`} className="text-sm text-stone-600 mb-2 line-clamp-2">
                  {p.descricao_curta}
                </div>
              )}
              <div className="text-sm text-stone-500">{p.endereco || "Endereço não informado"}</div>
              <div className="text-sm text-stone-500">{p.telefone || "—"}</div>
              <div className="text-sm text-stone-500 truncate">{p.email || "—"}</div>
              {Array.isArray(p.comodidades) && p.comodidades.length > 0 && (
                <div data-testid={`pousada-comodidades-${p.id}`} className="mt-3 flex flex-wrap gap-1">
                  {p.comodidades.slice(0, 4).map((slug) => {
                    const c = COMODIDADES_POUSADA.find((x) => x.slug === slug);
                    const label = c ? c.label : slug;
                    return (
                      <span key={slug} className="text-[10px] uppercase tracking-widest bg-[#EAF0EA] text-[#083A6B] px-2 py-0.5 rounded-full">
                        {label}
                      </span>
                    );
                  })}
                  {p.comodidades.length > 4 && (
                    <span className="text-[10px] uppercase tracking-widest text-stone-500 px-2 py-0.5">
                      +{p.comodidades.length - 4}
                    </span>
                  )}
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-stone-200 flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-stone-400">Quartos</span>
                <span className="flex items-center gap-2 font-serif text-xl text-stone-900">
                  {quartosMap[p.id] || 0}
                  <ChevronRight size={16} className="text-stone-400 group-hover:text-[#083A6B] transition-colors" />
                </span>
              </div>
              {(() => {
                const s = pousadaStats[p.id] || {};
                const ag = s.aguardando_limpeza || 0;
                const em = s.em_limpeza || 0;
                const lib = s.aguardando_liberacao || 0;
                const bch = s.bloqueados_chacara || 0;
                if (ag + em + lib + bch === 0) return null;
                return (
                  <div data-testid={`pousada-limpeza-badges-${p.id}`} className="mt-3 flex flex-wrap gap-1.5">
                    {ag > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#FEE7DE] text-[#B4472B] px-2 py-1 rounded-full">
                        Aguardando · {ag}
                      </span>
                    )}
                    {em > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#DBE7F2] text-[#1E4C7A] px-2 py-1 rounded-full">
                        Em limpeza · {em}
                      </span>
                    )}
                    {lib > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#E9DFEF] text-[#5E3B7A] px-2 py-1 rounded-full">
                        Liberar · {lib}
                      </span>
                    )}
                    {bch > 0 && (
                      <span
                        data-testid={`pousada-bloqueio-chacara-${p.id}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#EAF0EA] text-[#083A6B] px-2 py-1 rounded-full"
                      >
                        <Trees size={10} /> Chácara · {bch}
                      </span>
                    )}
                  </div>
                );
              })()}
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
